/*
 * infinite-options-accordion.js
 *
 * Clip-and-reveal treatment for the Infinite Options by ShopPad app block
 * (and the legacy ShopPad Product Personalizer). When the container has
 * more than FIELD_THRESHOLD personalisation fields, clip it to a fixed
 * height, add a gradient fade, and show a "Personalise & Buy" button that
 * removes the clip on tap.
 *
 * The injected button is tagged with `accordian-reveal` so sticky-cta.js
 * can remove it when the sticky CTA is pressed.
 *
 * Loaded with `defer`, so the DOM is parsed by the time this runs. Third
 * party apps (ShopPad IO / Personalizer) render their fields asynchronously,
 * so we also watch the DOM for the container appearing after load.
 */

{
  const CONTAINER_SELECTORS = ['#infiniteoptions-container', '.product-personalizer'];

  const FIELD_SELECTOR = [
    ':scope > div',
    ':scope > label',
    '.shoppad--io--option',
    '.pplr-wrapper',
    '.spb-productoption-wrap',
    '.spb-productoptionswatchwrapper',
  ].join(',');

  const FIELD_THRESHOLD = 4;
  const OBSERVER_TIMEOUT_MS = 15_000;

  const HEADING_CLASS = 'personalisation-heading';
  const REVEAL_CLASS = 'reveal';
  const COLLAPSED_FLAG_CLASS = 'io-fields-collapsed';
  const BUTTON_CLASS = 'reveal-button accordian-reveal';

  const findContainer = () =>
    CONTAINER_SELECTORS.reduce(
      (found, selector) => found ?? document.querySelector(selector),
      null,
    );

  const countFields = (container) => container.querySelectorAll(FIELD_SELECTOR).length;

  const insertHeading = (container) => {
    const parent = container.parentNode;
    if (!parent || parent.querySelector(`.${HEADING_CLASS}`)) return;

    const heading = document.createElement('div');
    heading.className = `${HEADING_CLASS} font-semibold text-lg`;
    heading.textContent = 'Personalise';
    parent.insertBefore(heading, container);
  };

  const applyReveal = (container) => {
    if (container.classList.contains(REVEAL_CLASS)) return;

    container.classList.add(REVEAL_CLASS);
    document.documentElement.classList.add(COLLAPSED_FLAG_CLASS);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = BUTTON_CLASS;
    button.textContent = 'Personalise & Buy';
    container.appendChild(button);

    const controller = new AbortController();
    const { signal } = controller;

    const dismiss = (event) => {
      if (event?.type === 'click' && event.target === button) {
        event.preventDefault();
      }
      container.classList.remove(REVEAL_CLASS);
      document.documentElement.classList.remove(COLLAPSED_FLAG_CLASS);
      button.remove();
      controller.abort();
    };

    // The reveal button has a direct click handler. For the rest of the
    // container, dismiss on the first real form interaction (change/input)
    // or on any click in the bubble phase. Bubble phase (not capture) is
    // deliberate: ShopPad's swatch click handlers run first, so the user's
    // selection lands, and then we expand. Capture phase ran before those
    // handlers and could swallow first taps. change/input alone is not
    // enough because swatch fields are <div>/<button> based and don't emit
    // native change/input on the container.
    button.addEventListener('click', dismiss, { signal, once: true });
    container.addEventListener('click', dismiss, { signal });
    container.addEventListener('change', dismiss, { signal });
    container.addEventListener('input', dismiss, { signal });
  };

  let initialised = false;

  const tryInit = () => {
    if (initialised) return true;

    const container = findContainer();
    if (!container || countFields(container) === 0) return false;

    initialised = true;
    insertHeading(container);
    if (countFields(container) > FIELD_THRESHOLD) {
      applyReveal(container);
    }
    return true;
  };

  const start = () => {
    if (tryInit()) return;

    const observer = new MutationObserver(() => {
      if (tryInit()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Safety: stop observing after a while to avoid running indefinitely on
    // pages that never render personalisation fields.
    setTimeout(() => observer.disconnect(), OBSERVER_TIMEOUT_MS);
  };

  start();
}
